import { useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useCallback, useEffect } from 'react'
import {
  AuftragOption,
  AuftragOptionen,
  BefehlFormAuftrag,
  BefehlFormField,
  ReadOnlyText
} from '../Generic'
import type { RadioChipOption } from '../../form'
import {
  BetriebsstelleField,
  BetriebsstellenartField,
  Input,
  RadioChipGroup
} from '../../form'
import type { Befehl5FormData } from './schema'
import BorderedBox from '../Generic/BorderedBox'
import ZugmeldestelleWrapper from '../Generic/ZugmeldestelleWrapper'

export interface Auftrag5_41FormProps {
  readOnly: boolean
  'data-testid': string
  activateEmptyFieldWarnings?: boolean
}

export default function Auftrag5_41Form({
  readOnly,
  'data-testid': testId,
  activateEmptyFieldWarnings = false
}: Readonly<Auftrag5_41FormProps>) {
  const formMethods = useFormContext<Befehl5FormData>()
  const { control, formState, watch, setValue } = formMethods
  const { errors } = formState
  const { t } = useTranslation('ui-befehle')

  const auftragEnabled = true
  const selector = watch('auftrag_5_41.selector')
  const feld_5_47_type = watch('auftrag_5_41.feld_5_47_type')
  const feld_5_48_type = watch('auftrag_5_41.feld_5_48_type')
  const x43Betriebsstellenart = watch(
    'auftrag_5_41.feld_5_43_zugmeldestelle.art'
  )
  const x44Betriebsstellenart = watch(
    'auftrag_5_41.feld_5_44_zugmeldestelle.art'
  )

  const clearConflictingX47Fields = useCallback(
    (newType: string) => {
      if (newType === 'km') {
        setValue('auftrag_5_41.feld_5_47_2_signal', '', {
          shouldValidate: false,
          shouldDirty: false
        })
      } else if (newType === 'signal') {
        setValue('auftrag_5_41.feld_5_47_1_km', '', {
          shouldValidate: false,
          shouldDirty: false
        })
      }
    },
    [setValue]
  )

  const clearConflictingX48Fields = useCallback(
    (newType: string) => {
      if (newType === 'km') {
        setValue('auftrag_5_41.feld_5_48_2_signal', '', {
          shouldValidate: false,
          shouldDirty: false
        })
      } else if (newType === 'signal') {
        setValue('auftrag_5_41.feld_5_48_1_km', '', {
          shouldValidate: false,
          shouldDirty: false
        })
      }
    },
    [setValue]
  )

  const onX47TypeChange = useCallback(
    (newValue: string) => {
      setValue('auftrag_5_41.feld_5_47_type', newValue as '' | 'km' | 'signal')
      clearConflictingX47Fields(newValue)
    },
    [setValue, clearConflictingX47Fields]
  )

  const onX48TypeChange = useCallback(
    (newValue: string) => {
      setValue('auftrag_5_41.feld_5_48_type', newValue as '' | 'km' | 'signal')
      clearConflictingX48Fields(newValue)
    },
    [setValue, clearConflictingX48Fields]
  )

  useEffect(() => {
    if (selector === '') {
      setValue('auftrag_5_41.feld_5_43_zugmeldestelle.name', '', {
        shouldValidate: false,
        shouldDirty: false
      })
      setValue('auftrag_5_41.feld_5_44_zugmeldestelle.name', '', {
        shouldValidate: false,
        shouldDirty: false
      })
      setValue('auftrag_5_41.feld_5_43_zugmeldestelle.art', '', {
        shouldValidate: false,
        shouldDirty: false
      })
      setValue('auftrag_5_41.feld_5_44_zugmeldestelle.art', '', {
        shouldValidate: false,
        shouldDirty: false
      })
    } else if (selector === 'in') {
      setValue('auftrag_5_41.feld_5_44_zugmeldestelle.name', '', {
        shouldValidate: false,
        shouldDirty: false
      })
      setValue('auftrag_5_41.feld_5_44_zugmeldestelle.art', '', {
        shouldValidate: false,
        shouldDirty: false
      })
    }
  }, [selector, setValue])

  return (
    <BefehlFormAuftrag
      auftragKennung="5.41"
      fieldName="auftrag_5_41.enabled"
      readOnly={true}
      disabled={false}
      testId={testId}
    >
      <div className="flex flex-col md:flex-row md:flex-wrap md:items-baseline gap-2 md:gap-x-1 md:gap-y-2">
        <span data-testid={`${testId}-text`}>{t('auftraege.5_41.label')}</span>
        <div className="flex items-baseline gap-0.5">
          <div className="flex flex-col">
            <BefehlFormField
              name="auftrag_5_41.feld_5_42_1_kmh"
              control={control}
              label="km/h"
              readOnly={readOnly}
              disabled={false}
              activateEmptyWarning={activateEmptyFieldWarnings}
              data-testid={`${testId}-feld-5-42-1`}
              className="min-w-15"
              render={({ field, isDisabled }) => (
                <Input
                  {...field}
                  label="km/h"
                  disabled={isDisabled}
                  errors={errors}
                  data-testid={`${testId}-feld-5-42-1-inner`}
                />
              )}
            />
            <label data-testid={`${testId}-feld-5-42-1-label`}>
              {auftragEnabled ? '5' : 'x'}.42.1 km/h
            </label>
          </div>
        </div>
      </div>

      <div className="flex-1">
        <div className="w-full">
          <div className="flex flex-col gap-1">
            {/* zwischen/in selector with Zugmeldestellen */}
            <div className="@container flex flex-col">
              <div className="flex flex-col @[400px]:flex-row @[400px]:items-baseline gap-0.5">
                <div className="@[400px]:w-8 @[400px]:text-right flex justify-start @[400px]:justify-end items-baseline">
                  <BefehlFormField
                    name="auftrag_5_41.selector"
                    control={control}
                    label=""
                    readOnly={readOnly}
                    disabled={false}
                    activateEmptyWarning={false}
                    useTextDisplayInReadOnly={false}
                    data-testid={`${testId}-feld-5-43-44-selector`}
                    render={({ field, isDisabled }) => {
                      const options: RadioChipOption[] = [
                        {
                          value: 'zwischen',
                          label: t('auftraege.5_41.zwischen')
                        },
                        {
                          value: 'in',
                          label: t('auftraege.5_41.in')
                        }
                      ]
                      return (
                        <RadioChipGroup
                          name={field.name}
                          value={field.value}
                          onChange={field.onChange}
                          options={options}
                          disabled={isDisabled}
                          readOnly={readOnly}
                          showOnlySelected={readOnly}
                          testId={`${testId}-feld-5-43-44-selector-inner`}
                        />
                      )
                    }}
                  />
                </div>
                <div className="flex-1">
                  {selector === '' ? (
                    <div className="border border-cool-gray-200 px-1 py-1">
                      <ReadOnlyText
                        value={
                          readOnly
                            ? ''
                            : t('auftraege.5_41.placeholder_select_in_zwischen')
                        }
                        label=""
                        variant="solid"
                        activateEmptyWarning={activateEmptyFieldWarnings}
                        data-testid={`${testId}-feld-5-43-44-placeholder`}
                      />
                    </div>
                  ) : (
                    <BorderedBox
                      data-testid={`${testId}-feld-5-43-border-box`}
                      className={
                        selector === 'zwischen' ? '@[400px]:border-b-0' : ''
                      }
                      footer={
                        <span data-testid={`${testId}-feld-5-43-label`}>
                          {auftragEnabled ? '5' : 'x'}.43{' '}
                          {t('auftraege.5_41.x_43_label')}
                        </span>
                      }
                    >
                      <ZugmeldestelleWrapper
                        betriebsstellenartField={
                          <BefehlFormField
                            name="auftrag_5_41.feld_5_43_zugmeldestelle.art"
                            control={control}
                            label={t('auftraege.5_41.betriebsstellenart_label')}
                            readOnly={readOnly}
                            disabled={false}
                            activateEmptyWarning={activateEmptyFieldWarnings}
                            useTextDisplayInReadOnly={true}
                            data-testid={`${testId}-feld-5-43-art`}
                            render={({ field, isDisabled, isReadOnly }) => (
                              <BetriebsstellenartField
                                {...field}
                                label={t(
                                  'auftraege.5_41.betriebsstellenart_label'
                                )}
                                variant="solid"
                                errors={errors}
                                disabled={isDisabled}
                                readOnly={isReadOnly}
                                data-testid={`${testId}-feld-5-43-art-inner`}
                                selectClassName="w-full"
                              />
                            )}
                          />
                        }
                        bestriebsstelleField={
                          <BefehlFormField
                            name="auftrag_5_41.feld_5_43_zugmeldestelle.name"
                            control={control}
                            label={t('auftraege.5_41.x_43_label')}
                            readOnly={readOnly}
                            disabled={false}
                            activateEmptyWarning={activateEmptyFieldWarnings}
                            data-testid={`${testId}-feld-5-43-name`}
                            render={({ field, isDisabled, isReadOnly }) => (
                              <BetriebsstelleField
                                {...field}
                                label={t('auftraege.5_41.x_43_label')}
                                errors={errors}
                                disabled={isDisabled}
                                readOnly={isReadOnly}
                                typ={x43Betriebsstellenart}
                                data-testid={`${testId}-feld-5-43-name-inner`}
                              />
                            )}
                          />
                        }
                      />
                    </BorderedBox>
                  )}
                </div>
              </div>
              {/* x.44 field - only visible when selector is 'zwischen' */}
              <div
                className={
                  selector === 'zwischen' ? '@container' : '@container hidden'
                }
              >
                <div className="flex flex-col @[400px]:flex-row @[400px]:items-baseline gap-0.5">
                  <div className="mt-0.5 @[400px]:w-8 @[400px]:text-right">
                    <span data-testid={`${testId}-text-und`}>
                      {t('auftraege.5_41.und_label')}
                    </span>
                  </div>
                  <div className="flex-1">
                    <BorderedBox
                      className="@[400px]:border-t-0"
                      data-testid={`${testId}-feld-5-44-border-box`}
                      footer={
                        <span data-testid={`${testId}-feld-5-44-label`}>
                          {auftragEnabled ? '5' : 'x'}.44{' '}
                          {t('auftraege.5_41.x_44_label')}
                        </span>
                      }
                    >
                      <ZugmeldestelleWrapper
                        betriebsstellenartField={
                          <BefehlFormField
                            name="auftrag_5_41.feld_5_44_zugmeldestelle.art"
                            control={control}
                            label={t('auftraege.5_41.betriebsstellenart_label')}
                            readOnly={readOnly}
                            disabled={false}
                            activateEmptyWarning={activateEmptyFieldWarnings}
                            useTextDisplayInReadOnly={true}
                            data-testid={`${testId}-feld-5-44-art`}
                            render={({ field, isDisabled, isReadOnly }) => (
                              <BetriebsstellenartField
                                {...field}
                                label={t(
                                  'auftraege.5_41.betriebsstellenart_label'
                                )}
                                variant="solid"
                                errors={errors}
                                disabled={isDisabled}
                                readOnly={isReadOnly}
                                data-testid={`${testId}-feld-5-44-art-inner`}
                                selectClassName="w-full"
                              />
                            )}
                          />
                        }
                        bestriebsstelleField={
                          <BefehlFormField
                            name="auftrag_5_41.feld_5_44_zugmeldestelle.name"
                            control={control}
                            label={t('auftraege.5_41.x_44_label')}
                            readOnly={readOnly}
                            disabled={false}
                            activateEmptyWarning={activateEmptyFieldWarnings}
                            data-testid={`${testId}-feld-5-44-name`}
                            render={({ field, isDisabled, isReadOnly }) => (
                              <BetriebsstelleField
                                {...field}
                                label={t('auftraege.5_41.x_44_label')}
                                errors={errors}
                                disabled={isDisabled}
                                readOnly={isReadOnly}
                                typ={x44Betriebsstellenart}
                                data-testid={`${testId}-feld-5-44-name-inner`}
                              />
                            )}
                          />
                        }
                      />
                    </BorderedBox>
                  </div>
                </div>
              </div>
            </div>

            {/* von field */}
            <div className="@container">
              <div className="flex flex-col @[400px]:flex-row @[400px]:items-baseline gap-0.5">
                <div className="@[400px]:w-8 @[400px]:text-right">
                  <span data-testid={`${testId}-text-von`}>
                    {t('auftraege.5_41.von_label')}
                  </span>
                </div>
                <div className="flex-1">
                  <BefehlFormField
                    name="auftrag_5_41.feld_5_47_type"
                    control={control}
                    label=""
                    readOnly={false}
                    disabled={false}
                    activateEmptyWarning={false}
                    useTextDisplayInReadOnly={false}
                    data-testid={`${testId}-feld-5-47-selector`}
                    render={({ field, isDisabled }) => (
                      <AuftragOptionen
                        name="auftrag_5_41.feld_5_47_type"
                        value={field.value}
                        onChange={(newValue) => onX47TypeChange(newValue)}
                        readOnly={readOnly}
                        disabled={isDisabled}
                        errors={errors}
                        data-testid={`${testId}-feld-5-47-selector-options`}
                      >
                        <AuftragOption
                          value="km"
                          label={`${auftragEnabled ? '5' : 'x'}.47.1 km`}
                        >
                          <div className="flex flex-col lg:flex-row gap-1">
                            <div className="w-full">
                              <BefehlFormField
                                name="auftrag_5_41.feld_5_47_1_km"
                                control={control}
                                label={t('auftraege.5_41.km_label')}
                                readOnly={readOnly}
                                disabled={feld_5_47_type !== 'km'}
                                activateEmptyWarning={
                                  activateEmptyFieldWarnings
                                }
                                data-testid={`${testId}-feld-5-47-1`}
                                render={({ field: kmField, isDisabled }) => (
                                  <Input
                                    {...kmField}
                                    label={t('auftraege.5_41.km_label')}
                                    disabled={isDisabled}
                                    errors={errors}
                                    data-testid={`${testId}-feld-5-47-1-inner`}
                                  />
                                )}
                              />
                            </div>
                          </div>
                        </AuftragOption>
                        <AuftragOption
                          value="signal"
                          label={`${auftragEnabled ? '5' : 'x'}.47.2 Signal`}
                        >
                          <div className="flex flex-col lg:flex-row gap-1">
                            <div className="w-full">
                              <BefehlFormField
                                name="auftrag_5_41.feld_5_47_2_signal"
                                control={control}
                                label={t('auftraege.5_41.signal_label')}
                                readOnly={readOnly}
                                disabled={feld_5_47_type !== 'signal'}
                                activateEmptyWarning={
                                  activateEmptyFieldWarnings
                                }
                                data-testid={`${testId}-feld-5-47-2`}
                                render={({
                                  field: signalField,
                                  isDisabled
                                }) => (
                                  <Input
                                    {...signalField}
                                    label={t('auftraege.5_41.signal_label')}
                                    disabled={isDisabled}
                                    errors={errors}
                                    data-testid={`${testId}-feld-5-47-2-inner`}
                                  />
                                )}
                              />
                            </div>
                          </div>
                        </AuftragOption>
                      </AuftragOptionen>
                    )}
                  />
                </div>
              </div>
            </div>

            {/* bis field */}
            <div className="@container">
              <div className="flex flex-col @[400px]:flex-row @[400px]:items-baseline gap-0.5">
                <div className="@[400px]:w-8 @[400px]:text-right">
                  <span data-testid={`${testId}-text-bis`}>
                    {t('auftraege.5_41.bis_label')}
                  </span>
                </div>
                <div className="flex-1">
                  <BefehlFormField
                    name="auftrag_5_41.feld_5_48_type"
                    control={control}
                    label=""
                    readOnly={false}
                    disabled={false}
                    activateEmptyWarning={false}
                    useTextDisplayInReadOnly={false}
                    data-testid={`${testId}-feld-5-48-selector`}
                    render={({ field, isDisabled }) => (
                      <AuftragOptionen
                        name="auftrag_5_41.feld_5_48_type"
                        value={field.value}
                        onChange={(newValue) => onX48TypeChange(newValue)}
                        readOnly={readOnly}
                        disabled={isDisabled}
                        errors={errors}
                        data-testid={`${testId}-feld-5-48-selector-options`}
                      >
                        <AuftragOption
                          value="km"
                          label={`${auftragEnabled ? '5' : 'x'}.48.1 km`}
                        >
                          <div className="flex flex-col lg:flex-row gap-1">
                            <div className="w-full">
                              <BefehlFormField
                                name="auftrag_5_41.feld_5_48_1_km"
                                control={control}
                                label={t('auftraege.5_41.km_label')}
                                readOnly={readOnly}
                                disabled={feld_5_48_type !== 'km'}
                                activateEmptyWarning={
                                  activateEmptyFieldWarnings
                                }
                                data-testid={`${testId}-feld-5-48-1`}
                                render={({ field: kmField, isDisabled }) => (
                                  <Input
                                    {...kmField}
                                    label={t('auftraege.5_41.km_label')}
                                    disabled={isDisabled}
                                    errors={errors}
                                    data-testid={`${testId}-feld-5-48-1-inner`}
                                  />
                                )}
                              />
                            </div>
                          </div>
                        </AuftragOption>
                        <AuftragOption
                          value="signal"
                          label={`${auftragEnabled ? '5' : 'x'}.48.2 Signal`}
                        >
                          <div className="flex flex-col lg:flex-row gap-1">
                            <div className="w-full">
                              <BefehlFormField
                                name="auftrag_5_41.feld_5_48_2_signal"
                                control={control}
                                label={t('auftraege.5_41.signal_label')}
                                readOnly={readOnly}
                                disabled={feld_5_48_type !== 'signal'}
                                activateEmptyWarning={
                                  activateEmptyFieldWarnings
                                }
                                data-testid={`${testId}-feld-5-48-2`}
                                render={({
                                  field: signalField,
                                  isDisabled
                                }) => (
                                  <Input
                                    {...signalField}
                                    label={t('auftraege.5_41.signal_label')}
                                    disabled={isDisabled}
                                    errors={errors}
                                    data-testid={`${testId}-feld-5-48-1-inner`}
                                  />
                                )}
                              />
                            </div>
                          </div>
                        </AuftragOption>
                      </AuftragOptionen>
                    )}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </BefehlFormAuftrag>
  )
}
